// components/Editorial.tsx
import cx from 'clsx';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import styles from './Editorial.module.scss';
import { WPSimplePage } from 'types/pages';
import DOMPurify from 'dompurify';
import { Typography } from 'components/elements';
import { FormattedText } from '..';
import { WPAcfBasic } from '../../../types/acf';

interface OwnProps {
  data: WPSimplePage<WPAcfBasic>;
}

type EditorialProps = JSX.IntrinsicElements['div'] & OwnProps;

const FRONTEND_URL = process.env.NEXT_PUBLIC_FRONTEND_URL || 'http://localhost:3000';

const isInternalUrl = (href: string): boolean => {
  if (!href) return false;

  try {
    const url = new URL(href);
    const frontendHostname = new URL(FRONTEND_URL).hostname;
    const landoHostnameSuffix = '.lndo.site';

    return (
        url.hostname === frontendHostname ||
        url.hostname.endsWith(landoHostnameSuffix) ||
        url.hostname === 'testarmy.com'
    );
  } catch (e) {
    return href.startsWith('/') && !href.includes(':');
  }
};

const Editorial: FunctionComponent<EditorialProps> = props => {
  const { data: page, className, ...rest } = props;
  const { title, content } = page;

  const contentRef = useRef<HTMLDivElement>(null);

  const articleClassName = cx({
    [styles.page]: true,
    ...(className && { [className]: true }),
  });

  useEffect(() => {
    const contentElement = contentRef.current;

    const handleClick = (event: Event) => {
      const target = (event.target as HTMLElement)?.closest('a');

      setTimeout(() => {
        console.log('Clicked link:', target);
      }, 10000);

      if (target) {
        const href = target.getAttribute('href');
        if (!href) return;

        const isInternal = isInternalUrl(href);

        if (!isInternal) {
          event.preventDefault();
          window.open(href, '_blank');
          target.setAttribute('rel', 'noopener noreferrer');
        }
      }
    };

    if (contentElement) {
      contentElement.addEventListener('click', handleClick as EventListener);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('click', handleClick as EventListener);
      }
    };
  }, []);

  return (
      <div className={articleClassName} {...rest}>
        <Typography
            className={styles.title}
            mobile="new_neue_semibold_26"
            tablet="new_neue_semibold_26"
            desktop="new_neue_32_h1"
            as="h1"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title.rendered) }}
        />
        <FormattedText
            className={styles.content}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.rendered) }}
            ref={contentRef}
        />
      </div>
  );
};

export default Editorial;
